// Touch input source + on-screen overlay for mobile devices. Polled like
// Gamepad — produces analog moveX/moveZ from a floating joystick, yaw/pitch
// deltas from right-side aim drags, and an InputBtn bitmask from on-screen
// action buttons. Owns its DOM under the persistent #mobile-controls div.

import { InputBtn } from './InputFrame';

const JOY_RADIUS = 60;            // px from anchor at full deflection
const AIM_GAIN = 0.0025;          // matches mouse: yaw -= dx * 0.0025
const AIM_MAX_DELTA_PX = 200;     // per-frame clamp — guards against batched touchmoves

type Zone = 'joystick' | 'aim' | 'btn-punch' | 'btn-grab' | 'btn-jump' | 'btn-kick' | 'btn-sprint';

interface ActiveTouch {
  zone: Zone;
  // joystick: anchor coordinates
  ax?: number; ay?: number;
  // joystick / aim: most recent x/y for delta calculation
  lx?: number; ly?: number;
}

export interface TouchPollResult {
  moveX: number;
  moveZ: number;
  yawDelta: number;
  pitchDelta: number;
  buttons: number;
  active: boolean;
}

export class TouchControls {
  private root: HTMLElement;
  private joyBase!: HTMLElement;
  private joyThumb!: HTMLElement;
  private btnPunch!: HTMLElement;
  private btnGrab!: HTMLElement;
  private btnJump!: HTMLElement;
  private btnKick!: HTMLElement;
  private btnSprint!: HTMLElement;
  private btnSpectateL!: HTMLElement;
  private btnSpectateR!: HTMLElement;
  private btnMenu!: HTMLElement;
  private styleEl: HTMLStyleElement;

  // Per-touch state, keyed by Touch.identifier.
  private touches = new Map<number, ActiveTouch>();

  // Continuous state.
  private moveX = 0;
  private moveZ = 0;
  // Aim deltas accumulate from touchmoves and are drained each poll().
  private aimDxAccum = 0;
  private aimDyAccum = 0;
  // Held-button state from currently-active button touches.
  private heldButtons = 0;

  // Edge callbacks driven from button taps.
  onSpectateLeft: (() => void) | null = null;
  onSpectateRight: (() => void) | null = null;
  onMenuTap: (() => void) | null = null;

  constructor() {
    const root = document.getElementById('mobile-controls');
    if (!root) throw new Error('TouchControls: #mobile-controls element missing from DOM');
    this.root = root;

    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLE_BLOCK;
    document.head.appendChild(this.styleEl);

    this.root.innerHTML = MARKUP;
    const q = (sel: string): HTMLElement => {
      const el = this.root.querySelector(sel);
      if (!el) throw new Error(`TouchControls: missing ${sel}`);
      return el as HTMLElement;
    };
    this.joyBase = q('.mc-joy-base');
    this.joyThumb = q('.mc-joy-thumb');
    this.btnPunch = q('[data-action="punch"]');
    this.btnGrab = q('[data-action="grab"]');
    this.btnJump = q('[data-action="jump"]');
    this.btnKick = q('[data-action="kick"]');
    this.btnSprint = q('[data-action="sprint"]');
    this.btnSpectateL = q('[data-action="spectate-l"]');
    this.btnSpectateR = q('[data-action="spectate-r"]');
    this.btnMenu = q('[data-action="menu"]');

    // Per-zone touchstart listeners — each lets us tag the new touch with its
    // owning zone. Subsequent move/end live on window so the finger drifting
    // off-element doesn't break tracking.
    q('.mc-aim').addEventListener('touchstart', this.onAimStart, { passive: false });
    q('.mc-joy').addEventListener('touchstart', this.onJoyStart, { passive: false });
    this.btnPunch.addEventListener('touchstart', (e) => this.onBtnStart(e, 'btn-punch'), { passive: false });
    this.btnGrab.addEventListener('touchstart', (e) => this.onBtnStart(e, 'btn-grab'), { passive: false });
    this.btnJump.addEventListener('touchstart', (e) => this.onBtnStart(e, 'btn-jump'), { passive: false });
    this.btnKick.addEventListener('touchstart', (e) => this.onBtnStart(e, 'btn-kick'), { passive: false });
    this.btnSprint.addEventListener('touchstart', (e) => this.onBtnStart(e, 'btn-sprint'), { passive: false });
    this.btnSpectateL.addEventListener('touchstart', this.onSpectateLeftStart, { passive: false });
    this.btnSpectateR.addEventListener('touchstart', this.onSpectateRightStart, { passive: false });
    this.btnMenu.addEventListener('touchstart', this.onMenuStart, { passive: false });

    addEventListener('touchmove', this.onTouchMove, { passive: false });
    addEventListener('touchend', this.onTouchEnd);
    addEventListener('touchcancel', this.onTouchEnd);
  }

  dispose(): void {
    // The per-element touchstart listeners are dropped implicitly when we
    // clear root.innerHTML below — the elements (and their listener edges)
    // get garbage-collected. The window-level touchmove/end/cancel listeners
    // we attached must be removed explicitly.
    removeEventListener('touchmove', this.onTouchMove);
    removeEventListener('touchend', this.onTouchEnd);
    removeEventListener('touchcancel', this.onTouchEnd);

    this.root.innerHTML = '';
    this.styleEl.remove();
    this.touches.clear();
  }

  setVisible(v: boolean): void {
    this.root.classList.toggle('mc-hidden', !v);
    if (!v) {
      // Drop any in-flight touch state so re-showing later starts clean.
      this.touches.clear();
      this.moveX = this.moveZ = 0;
      this.aimDxAccum = this.aimDyAccum = 0;
      this.heldButtons = 0;
      this.joyBase.classList.remove('active');
      this.btnSprint.classList.remove('pressed');
    }
  }

  setSpectateVisible(v: boolean): void {
    this.btnSpectateL.classList.toggle('mc-hidden', !v);
    this.btnSpectateR.classList.toggle('mc-hidden', !v);
  }

  /** The JumpKick binding triggers a kick when both feet are airborne and a
   *  headbutt when planted (see Character.ts). The label flips to match so
   *  the player isn't confused by a button that does two things. */
  setMidAir(midAir: boolean): void {
    const next = midAir ? 'KICK' : 'HEADBUTT';
    if (this.btnKick.textContent !== next) this.btnKick.textContent = next;
  }

  /** Punch button is reused as the throw trigger while the local player is
   *  double-handing a victim (Character.ts:877). Flip the label so the
   *  affordance matches what the press actually does. */
  setDoubleHolding(holding: boolean): void {
    const next = holding ? 'THROW' : 'PUNCH';
    if (this.btnPunch.textContent !== next) this.btnPunch.textContent = next;
  }

  poll(_fixedDt: number): TouchPollResult {
    // Drain accumulated aim deltas into a one-frame yaw/pitch delta with the
    // same scale as the mouse path. Clamp to guard against batched touchmoves.
    const dx = clamp(this.aimDxAccum, -AIM_MAX_DELTA_PX, AIM_MAX_DELTA_PX);
    const dy = clamp(this.aimDyAccum, -AIM_MAX_DELTA_PX, AIM_MAX_DELTA_PX);
    this.aimDxAccum = 0;
    this.aimDyAccum = 0;

    const buttons = this.heldButtons;
    const active = this.moveX !== 0 || this.moveZ !== 0 || dx !== 0 || dy !== 0 || buttons !== 0;

    return {
      moveX: this.moveX,
      moveZ: this.moveZ,
      yawDelta: -dx * AIM_GAIN,
      pitchDelta: dy * AIM_GAIN,
      buttons,
      active
    };
  }

  // ---- touch handlers ---------------------------------------------------

  private onAimStart = (e: TouchEvent): void => {
    e.preventDefault();
    for (let _i = 0; _i < e.changedTouches.length; _i++) { const t = e.changedTouches[_i]!;
      if (this.touches.has(t.identifier)) continue;
      this.touches.set(t.identifier, { zone: 'aim', lx: t.clientX, ly: t.clientY });
    }
  };

  private onJoyStart = (e: TouchEvent): void => {
    e.preventDefault();
    for (let _i = 0; _i < e.changedTouches.length; _i++) { const t = e.changedTouches[_i]!;
      if (this.touches.has(t.identifier)) continue;
      // Only one joystick touch at a time — if there's already one, ignore.
      let hasJoy = false;
      for (const v of this.touches.values()) if (v.zone === 'joystick') { hasJoy = true; break; }
      if (hasJoy) continue;
      this.touches.set(t.identifier, { zone: 'joystick', ax: t.clientX, ay: t.clientY, lx: t.clientX, ly: t.clientY });
      this.showJoystickAt(t.clientX, t.clientY);
    }
  };

  private onBtnStart = (e: TouchEvent, zone: Zone): void => {
    e.preventDefault();
    e.stopPropagation();
    for (let _i = 0; _i < e.changedTouches.length; _i++) { const t = e.changedTouches[_i]!;
      if (this.touches.has(t.identifier)) continue;
      this.touches.set(t.identifier, { zone });
      this.applyHeldButton(zone, true);
      this.btnFromZone(zone)?.classList.add('pressed');
    }
  };

  private onSpectateLeftStart = (e: TouchEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.onSpectateLeft?.();
    this.flashButton(this.btnSpectateL);
  };

  private onSpectateRightStart = (e: TouchEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.onSpectateRight?.();
    this.flashButton(this.btnSpectateR);
  };

  private onMenuStart = (e: TouchEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    this.onMenuTap?.();
    this.flashButton(this.btnMenu);
  };

  private onTouchMove = (e: TouchEvent): void => {
    let consumed = false;
    for (let _i = 0; _i < e.changedTouches.length; _i++) { const t = e.changedTouches[_i]!;
      const entry = this.touches.get(t.identifier);
      if (!entry) continue;
      consumed = true;
      if (entry.zone === 'joystick' && entry.ax != null && entry.ay != null) {
        const dxJ = t.clientX - entry.ax;
        const dyJ = t.clientY - entry.ay;
        const len = Math.hypot(dxJ, dyJ);
        const cap = Math.min(1, len / JOY_RADIUS);
        const ndx = len > 0 ? dxJ / len : 0;
        const ndy = len > 0 ? dyJ / len : 0;
        // Joystick up (dy < 0) = forward = moveZ < 0. Right (dx > 0) = strafe right.
        // Matches WASD: KeyW → moveZ -= 1, KeyD → moveX += 1. (Input.ts:149-152)
        this.moveX = ndx * cap;
        this.moveZ = ndy * cap;
        // Visual: thumb follows finger, capped to JOY_RADIUS from base.
        const thumbX = ndx * Math.min(len, JOY_RADIUS);
        const thumbY = ndy * Math.min(len, JOY_RADIUS);
        this.joyThumb.style.transform = `translate(calc(-50% + ${thumbX}px), calc(-50% + ${thumbY}px))`;
        entry.lx = t.clientX;
        entry.ly = t.clientY;
      } else if (entry.zone === 'aim') {
        const px = entry.lx ?? t.clientX;
        const py = entry.ly ?? t.clientY;
        this.aimDxAccum += t.clientX - px;
        this.aimDyAccum += t.clientY - py;
        entry.lx = t.clientX;
        entry.ly = t.clientY;
      }
      // Buttons don't track movement — finger sliding off a button doesn't
      // release it (committed-on-start semantics).
    }
    if (consumed) e.preventDefault();
  };

  private onTouchEnd = (e: TouchEvent): void => {
    for (let _i = 0; _i < e.changedTouches.length; _i++) { const t = e.changedTouches[_i]!;
      const entry = this.touches.get(t.identifier);
      if (!entry) continue;
      if (entry.zone === 'joystick') {
        this.moveX = 0;
        this.moveZ = 0;
        this.hideJoystick();
      } else if (entry.zone.startsWith('btn-')) {
        this.applyHeldButton(entry.zone, false);
        this.btnFromZone(entry.zone)?.classList.remove('pressed');
      }
      this.touches.delete(t.identifier);
    }
  };

  // ---- helpers ----------------------------------------------------------

  private applyHeldButton(zone: Zone, on: boolean): void {
    const bit = (
      zone === 'btn-punch'  ? InputBtn.Punch :
      zone === 'btn-grab'   ? InputBtn.Grab  :
      zone === 'btn-jump'   ? InputBtn.Jump  :
      zone === 'btn-kick'   ? InputBtn.JumpKick :
      zone === 'btn-sprint' ? InputBtn.Sprint :
      0
    );
    if (on) this.heldButtons |= bit;
    else this.heldButtons &= ~bit;
  }

  private btnFromZone(zone: Zone): HTMLElement | null {
    switch (zone) {
      case 'btn-punch': return this.btnPunch;
      case 'btn-grab': return this.btnGrab;
      case 'btn-jump': return this.btnJump;
      case 'btn-kick': return this.btnKick;
      case 'btn-sprint': return this.btnSprint;
      default: return null;
    }
  }

  private showJoystickAt(x: number, y: number): void {
    this.joyBase.style.left = `${x}px`;
    this.joyBase.style.top = `${y}px`;
    this.joyThumb.style.transform = `translate(-50%, -50%)`;
    this.joyBase.classList.add('active');
  }

  private hideJoystick(): void {
    this.joyBase.classList.remove('active');
    this.joyThumb.style.transform = `translate(-50%, -50%)`;
  }

  private flashButton(el: HTMLElement): void {
    el.classList.add('pressed');
    setTimeout(() => el.classList.remove('pressed'), 120);
  }
}

/** Initial detection: phone/tablet primary touch device. */
export function isTouchPrimaryDevice(): boolean {
  const coarse = matchMedia('(pointer: coarse)').matches;
  const noHover = !matchMedia('(hover: hover)').matches;
  const hasTouch = (navigator.maxTouchPoints ?? 0) > 0;
  return coarse || (hasTouch && noHover);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

const MARKUP = `
  <div class="mc-aim"></div>
  <div class="mc-joy"></div>
  <div class="mc-joy-base"><div class="mc-joy-thumb"></div></div>
  <div class="mc-buttons">
    <button class="mc-btn mc-btn-kick" data-action="kick" type="button">HEADBUTT</button>
    <button class="mc-btn mc-btn-jump" data-action="jump" type="button">JUMP</button>
    <button class="mc-btn mc-btn-punch" data-action="punch" type="button">PUNCH</button>
    <button class="mc-btn mc-btn-grab" data-action="grab" type="button">GRAB</button>
  </div>
  <button class="mc-btn mc-btn-sprint" data-action="sprint" type="button">SPRINT</button>
  <button class="mc-btn mc-spectate mc-spectate-l mc-hidden" data-action="spectate-l" type="button">◀</button>
  <button class="mc-btn mc-spectate mc-spectate-r mc-hidden" data-action="spectate-r" type="button">▶</button>
  <button class="mc-btn mc-menu" data-action="menu" type="button">⋯</button>
`;

const STYLE_BLOCK = `
#mobile-controls.mc-hidden { display: none; }

#mobile-controls .mc-aim {
  position: absolute; inset: 0;
  pointer-events: auto;
  touch-action: none;
}
#mobile-controls .mc-joy {
  position: absolute; left: 0; bottom: 0;
  width: 45vw; height: 55dvh;
  pointer-events: auto;
  touch-action: none;
}
#mobile-controls .mc-joy-base {
  position: absolute; width: 140px; height: 140px;
  border-radius: 50%;
  background: rgba(20, 24, 32, 0.32);
  border: 2px solid rgba(255, 255, 255, 0.18);
  transform: translate(-50%, -50%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 120ms ease;
}
#mobile-controls .mc-joy-base.active { opacity: 1; }
#mobile-controls .mc-joy-thumb {
  position: absolute; left: 50%; top: 50%;
  width: 64px; height: 64px;
  margin: -32px 0 0 -32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  border: 2px solid rgba(255, 255, 255, 0.85);
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

#mobile-controls .mc-buttons {
  position: absolute;
  right: calc(20px + env(safe-area-inset-right));
  bottom: calc(28px + env(safe-area-inset-bottom));
  width: 168px; height: 168px;
  pointer-events: none;
}

#mobile-controls .mc-btn {
  appearance: none; -webkit-appearance: none;
  border: 2px solid rgba(255, 255, 255, 0.55);
  border-radius: 50%;
  background: rgba(28, 32, 42, 0.45);
  color: rgba(255, 255, 255, 0.92);
  font: 700 13px 'Chewy', ui-sans-serif, system-ui, sans-serif;
  letter-spacing: 0.05em;
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
  transition: transform 80ms ease, background 80ms ease, opacity 160ms ease;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.32);
  display: flex; align-items: center; justify-content: center;
}
#mobile-controls .mc-btn.pressed {
  transform: scale(0.94);
  background: rgba(255, 200, 100, 0.55);
  color: #1a1a1a;
}

/* Right-side action stack — diamond layout for 4 buttons. */
#mobile-controls .mc-buttons .mc-btn {
  position: absolute; width: 72px; height: 72px;
}
#mobile-controls .mc-btn-punch {
  /* Primary fire: largest + hottest position (bottom-left of cluster). */
  width: 84px; height: 84px;
  left: 0; bottom: 36px;
  background: rgba(220, 86, 86, 0.55);
  border-color: rgba(255, 200, 200, 0.7);
}
#mobile-controls .mc-btn-grab {
  left: 88px; bottom: 0;
  background: rgba(86, 140, 220, 0.5);
  border-color: rgba(200, 220, 255, 0.65);
}
#mobile-controls .mc-btn-jump {
  left: 88px; bottom: 88px;
  background: rgba(120, 200, 120, 0.5);
  border-color: rgba(220, 255, 220, 0.65);
}
#mobile-controls .mc-btn-kick {
  /* Top-left of the cluster; heel-of-thumb reach. Label flips between
   * "KICK" (mid-air) and "HEADBUTT" (planted) so the longer word needs
   * a smaller font than the rest of the cluster. */
  left: 0; bottom: 124px;
  background: rgba(220, 160, 60, 0.5);
  border-color: rgba(255, 230, 180, 0.65);
  font-size: 11px;
  letter-spacing: 0.02em;
}

#mobile-controls .mc-btn-sprint {
  position: absolute;
  right: calc(206px + env(safe-area-inset-right));
  bottom: calc(28px + env(safe-area-inset-bottom));
  width: 64px; height: 64px;
  background: rgba(28, 32, 42, 0.45);
  font-size: 11px;
}

#mobile-controls .mc-spectate {
  position: absolute;
  bottom: calc(20px + env(safe-area-inset-bottom));
  width: 56px; height: 56px;
  font-size: 18px;
}
#mobile-controls .mc-spectate-l { left: calc(20px + env(safe-area-inset-left)); }
#mobile-controls .mc-spectate-r { left: calc(86px + env(safe-area-inset-left)); }
#mobile-controls .mc-spectate.mc-hidden { display: none; }

#mobile-controls .mc-menu {
  position: absolute;
  top: calc(16px + env(safe-area-inset-top));
  right: calc(16px + env(safe-area-inset-right));
  width: 48px; height: 48px;
  font-size: 22px;
  background: rgba(28, 32, 42, 0.4);
}

@media (max-height: 480px) {
  #mobile-controls .mc-buttons { width: 144px; height: 144px; }
  #mobile-controls .mc-buttons .mc-btn { width: 60px; height: 60px; }
  #mobile-controls .mc-btn-punch { width: 72px; height: 72px; bottom: 30px; }
  #mobile-controls .mc-btn-grab { left: 76px; }
  #mobile-controls .mc-btn-jump { left: 76px; bottom: 76px; }
  #mobile-controls .mc-btn-kick { bottom: 106px; }
  #mobile-controls .mc-btn-sprint { right: calc(180px + env(safe-area-inset-right)); width: 56px; height: 56px; }
}
`;
